import React, { useRef, useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { createPortal } from "react-dom";
import { slugify } from "../utils/stringTools";

// -------------------------------------------------------------
// PORTAL MENU (kebab)
// -------------------------------------------------------------
function PopupMenu({ position, onEdit, onDelete, onClose }) {
  const ref = useRef(null);

  // Fermer en cliquant en dehors
  useEffect(() => {
    const handleOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    };
    window.addEventListener("mousedown", handleOutside);
    return () => window.removeEventListener("mousedown", handleOutside);
  }, [onClose]);

  // Fermer en scrollant (horizontal ou vertical)
  useEffect(() => {
    const closeOnScroll = () => onClose();
    window.addEventListener("scroll", closeOnScroll, true);
    return () => window.removeEventListener("scroll", closeOnScroll, true);
  }, [onClose]);

  const style = {
    position: "absolute",
    top: position.top + 32,
    left: position.left - 155,
    zIndex: 9999,
    width: "180px",
    background: "#fff",
    border: "1px solid #dcdcdc",
    borderRadius: "6px",
    boxShadow: "0 3px 12px rgba(0,0,0,0.12)"
  };

  return createPortal(
    <div style={style} className="fb-tab-menu" ref={ref}>

      <button className="fb-tab-menu-close" onClick={onClose} >×</button>

      {/* Items */}
      <button className="dropdown-item" onClick={onEdit}>
        Modifier
      </button>

      <button className="dropdown-item text-danger" onClick={onDelete}>
        Supprimer
      </button>

    </div>,
    document.getElementById("fb-menu-root")
  );
}


// -------------------------------------------------------------
// MAIN COMPONENT
// -------------------------------------------------------------
export default function CanvasTabBar({ schema, activeTab, setActiveTab, setSchema, onActiveTabChange }) {
  const tabKeys = Object.keys(schema.properties || {});
  const [showModal, setShowModal] = useState(false);

  const [editingTab, setEditingTab] = useState(null);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [titleError, setTitleError] = useState("");
  const [slugError, setSlugError] = useState("");
  const [previousSlug, setPreviousSlug] = useState(null);

  const [menuData, setMenuData] = useState(null);

  const titleInputRef = useRef(null);

  // -------------------------------------------------------------
  // MODALE
  // -------------------------------------------------------------
  useEffect(() => {
    if (showModal && titleInputRef.current) {
      setTimeout(() => titleInputRef.current.focus(), 50);
    }
  }, [showModal]);

  const addTab = () => {
    const newTitle = `Onglet ${tabKeys.length + 1}`;
    setEditingTab(null);
    setTitle(newTitle);
    setSlug(slugify(newTitle));
    setShowModal(true);
  };

  const openEditModal = (tabId, tabData) => {
    setEditingTab(tabId);
    setTitle(tabData.title || "");
    setSlug(tabData.slug || slugify(tabData.title));
    setPreviousSlug(tabData.slug);
    setShowModal(true);
  };

  const validateSlug = (s) => {
    const clean = slugify(s.trim());
    if (!clean) return "Slug obligatoire.";
    if (/^[0-9]/.test(clean)) return "Ne pas commencer par un chiffre.";

    const duplicate = Object.entries(schema.properties || {}).find(
      ([key, tab]) =>
        tab.slug?.toLowerCase() === clean.toLowerCase() &&
        (!editingTab || editingTab !== key)
    );
    if (duplicate) return `Le slug "${clean}" existe déjà.`;

    return "";
  };

  const deleteTab = (tabId, title) => {
    if (!window.confirm(`Supprimer l’onglet "${title}" ?`)) return;

    setSchema((prev) => {
      const clone = structuredClone(prev);
      delete clone.properties[tabId];
      return clone;
    });

    if (activeTab === tabId) {
      const remain = Object.keys(schema.properties || {});
      setActiveTab(remain[0] || null);
    }
  };

  const handleSave = () => {
    const cleanTitle = title.trim();
    const cleanSlug = slugify(slug.trim());

    let err = false;

    if (!cleanTitle) {
      setTitleError("Titre obligatoire");
      err = true;
    }

    const slugVal = validateSlug(cleanSlug);
    if (slugVal) {
      setSlugError(slugVal);
      err = true;
    }

    if (err) return;

    setSchema((prev) => {
      const clone = structuredClone(prev);

      if (editingTab) {
        const tab = clone.properties[editingTab];
        if (tab) {
          const oldSlug = previousSlug || tab.slug;
          tab.title = cleanTitle;
          tab.slug = cleanSlug;

          if (oldSlug !== cleanSlug) {
            for (const [tKey, tDef] of Object.entries(clone.properties)) {
              for (const [fKey, field] of Object.entries(tDef.properties || {})) {
                if (Array.isArray(field["x-show-if"])) {
                  field["x-show-if"] = field["x-show-if"].map((cond) => {
                    if (cond.name?.startsWith(`${oldSlug}.`)) {
                      return {
                        ...cond,
                        name: cond.name.replace(`${oldSlug}.`, `${cleanSlug}.`)
                      };
                    }
                    return cond;
                  });
                }
              }
            }
          }
        }
      } else {
        const id = "tab_" + Date.now();
        clone.properties[id] = {
          type: "object",
          title: cleanTitle,
          slug: cleanSlug,
          properties: {},
          required: []
        };
        setActiveTab(id);
      }

      return clone;
    });

    setShowModal(false);
  };

  // -------------------------------------------------------------
  // DRAG & DROP horizontal
  // -------------------------------------------------------------
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(tabKeys);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    setSchema((prev) => {
      const clone = structuredClone(prev);
      const newProps = {};
      reordered.forEach((key) => (newProps[key] = clone.properties[key]));
      clone.properties = newProps;
      return clone;
    });
  };

  // -------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------
  return (
    <>
      <div className="fb-tabbar-wrapper">
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="tabs" direction="horizontal">
            {(provided) => (
              <div className="fb-tabbar" ref={provided.innerRef} {...provided.droppableProps}>
                {tabKeys.map((tabId, idx) => {
                  const tab = schema.properties[tabId];
                  const active = tabId === activeTab;

                  return (
                    <Draggable key={tabId} draggableId={tabId} index={idx}>
                      {(provided) => (
                        <div
                          className={`fb-tab-item ${active ? "active" : ""}`}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <div
                            className="fb-tab-label"
                            onClick={() => onActiveTabChange(tabId)}
                          >
                            {tab.title}
                          </div>

                          <button
                            type="button"
                            className="fb-tab-kebab"
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              setMenuData({
                                tabId,
                                top: rect.top + window.scrollY,
                                left: rect.left + window.scrollX
                              });
                            }}
                          >
                            ⋮
                          </button>
                        </div>
                      )}
                    </Draggable>
                  );
                })}

                {provided.placeholder}

                <button className="fb-tab-add me-5" onClick={addTab}>
                  +
                </button>
                <div>&nbsp;</div>
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {/* MENU KEbab via Portal */}
      {menuData && (
        <PopupMenu
          position={menuData}
          onEdit={() => {
            const tab = schema.properties[menuData.tabId];
            openEditModal(menuData.tabId, tab);
            setMenuData(null);
          }}
          onDelete={() => {
            const tab = schema.properties[menuData.tabId];
            deleteTab(menuData.tabId, tab.title);
            setMenuData(null);
          }}
          onClose={() => setMenuData(null)}
        />
      )}

      {/* MODALE */}
      {showModal && (
        <>
          <div className="modal fade show d-block">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">

                <div className="modal-header">
                  <div className="modal-title">
                    {editingTab ? "Modifier l’onglet" : "Nouvel onglet"}
                  </div>
                  <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
                </div>

                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Titre</label>
                    <input
                      type="text"
                      ref={titleInputRef}
                      className={`form-control ${titleError ? "is-invalid" : ""}`}
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                    {titleError && <div className="invalid-feedback">{titleError}</div>}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Slug</label>
                    <input
                      type="text"
                      className={`form-control ${slugError ? "is-invalid" : ""}`}
                      value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      onBlur={() => setSlugError(validateSlug(slug))}
                    />
                    {slugError && <div className="invalid-feedback">{slugError}</div>}
                  </div>
                </div>

                <div className="modal-footer">
                  <button className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>
                    Annuler
                  </button>
                  <button className="btn btn-primary" onClick={handleSave}>
                    Enregistrer
                  </button>
                </div>

              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show" />
        </>
      )}
    </>
  );
}
